import type { MouseEvent, ReactNode } from 'react';
import { navigateTo } from '../shell/keyboard.ts';
import styles from './AppLink.module.css';

/* ==========================================================================
   A navigation that is a link.

   Every destination in this console used to be a `<button>`: the nav rail,
   the tabs, the breadcrumb, the table rows, `View 3D`. The only `<a href>` in
   any of the thirteen routes was the skip link. For an internal tool whose
   working day consists of sending each other robot ids, that costs
   middle-click, ⌘-click, "copy link address", and the browser's own preview
   of where a control goes.

   The href is the truth; the click handler is the optimisation. Modified
   clicks and non-primary buttons fall through to the browser untouched, so a
   ⌘-click opens a second tab and a plain click stays in the SPA.
   ========================================================================== */
export function AppLink({
  href,
  children,
  className,
  title,
  onNavigate,
}: {
  href: string;
  children: ReactNode;
  className?: string | undefined;
  title?: string | undefined;
  /** Extra work the surface wants done on an in-SPA navigation only. */
  onNavigate?: (() => void) | undefined;
}) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    /* A link inside a clickable row: the link's destination wins. Without
       this the row's own handler also fires and the operator gets two
       navigations, or a selection they did not ask for behind the page they
       did. */
    e.stopPropagation();
    onNavigate?.();
    navigateTo(href);
  };
  return (
    <a
      href={href}
      className={className === undefined ? styles.link : `${styles.link} ${className}`}
      title={title}
      onClick={onClick}
    >
      {children}
    </a>
  );
}

/** `/fleet/detail/:id` — the one destination this console links to most. */
export function RobotLink({
  id,
  children,
  className,
  title,
}: {
  id: string;
  children?: ReactNode;
  className?: string | undefined;
  title?: string | undefined;
}) {
  return (
    <AppLink
      href={`/fleet/detail/${id}`}
      className={className}
      title={title ?? `Open ${id}`}
    >
      {children ?? id}
    </AppLink>
  );
}
